/**
 * The workspace's plans: `GET /api/v1/packages/` for the list, `POST` to
 * create one, and `GET`/`PATCH` on `/api/v1/packages/{public_id}/` for a
 * single one.
 *
 * Every one of them answers with the same record shape, so one mapping serves
 * all four, and the row the tab shows is built from the response rather than
 * from what was posted: the backend expands the duration id back into
 * `{ id, label }`, fixes the price to two decimal places, and sets `is_active`
 * and the timestamps itself. What the form thought it sent is not what the
 * list has to show.
 *
 * Two things do not travel as the form holds them:
 *
 *  - the duration goes up as its id and comes back as the whole object, which
 *    is why `BrandPlan` carries both the id and the label;
 *  - the price goes up as a decimal string - `"2999.00"` - and comes back as
 *    one, because that is how the backend keeps money. It is a number
 *    everywhere on this side of the wire.
 *
 * The edit sends only the fields that actually changed. That is what a `PATCH`
 * is for, and it keeps a save from writing back a stale copy of a field that
 * something else has since moved.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { PaginatedResponse } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import { formatCreatedOn } from "../planFormat";
import type {
  BrandPlan,
  NewBrandPlan,
  PlanRequest,
  PlanResponse,
  SupportChannel,
} from "../types/brand.types";

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/** Rupees as the backend keeps them: two decimal places, as a string. */
const toAmount = (price: number): string => price.toFixed(2);

/**
 * The row the tab lists, out of the record either verb answers with.
 *
 * A duration the response leaves out would be a plan no edit could send
 * back, so it degrades to an empty label and an id of 0 - which the dialog
 * refuses to submit - rather than to a wrong one.
 */
const toBrandPlan = (response: PlanResponse): BrandPlan => ({
  id: response.public_id,
  name: response.name ?? "",
  duration: response.duration?.label ?? "",
  durationId: response.duration?.id ?? 0,
  // `Number("")` is 0 and `Number(undefined)` is NaN, so both a missing price
  // and an unparseable one land on 0 rather than painting "₹ NaN" into a row.
  price: Number(response.price_amount) || 0,
  // The counts come back nullable and stay that way — see `BrandPlan`.
  // `?? null` is only for a response that left the key out altogether.
  consultations: response.consultations_count ?? null,
  mealPlans: response.meal_plans_count ?? null,
  mealOptionsPerMeal: response.meal_options_per_meal ?? null,
  supportChannels: response.support_channels ?? [],
  createdOn: formatCreatedOn(new Date(response.created_at)),
  status: response.is_active ? "Active" : "In-active",
});

/**
 * How many rows a page asks for. A hundred, because the tab wants the whole
 * list at once - it pages and searches in the browser - so the fewer round
 * trips the walk below takes, the better.
 */
const PAGE_SIZE = 100;

/**
 * Stops a broken `next` from looping forever. At a hundred a page this is
 * 2,000 plans, which is far past what any one practice sells.
 */
const MAX_PAGES = 20;

/** One page of plans, in the order the backend returned them. */
export async function fetchPlansPage(
  getToken: GetToken,
  page?: number,
  signal?: AbortSignal
): Promise<{ plans: BrandPlan[]; hasNext: boolean }> {
  const response = await api.get<PaginatedResponse<PlanResponse>>(
    API_ENDPOINTS.plans.list,
    {
      token: await token(getToken, "load your plans"),
      // Both are optional - left out, the backend serves its own first page.
      // `undefined` entries are dropped by the API client.
      query: { page, page_size: PAGE_SIZE },
      signal,
    }
  );

  return {
    plans: (response?.results ?? []).map(toBrandPlan),
    hasNext: !!response?.next,
  };
}

/**
 * Every plan the workspace has, walked a page at a time.
 *
 * The whole list rather than a page of it: the tab pages and searches over
 * what it holds, and until the endpoint can filter and sort for itself there
 * is nothing for a server-side page to be a page *of*. It moves to the server
 * the day either of those does.
 *
 * Sequential rather than parallel - the first response is what says whether
 * there is a second page at all.
 */
export async function fetchPlans(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<BrandPlan[]> {
  const plans: BrandPlan[] = [];

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const result = await fetchPlansPage(getToken, page, signal);
    plans.push(...result.plans);

    // An empty page as well as `next: null` ends the walk - a page that says
    // there is another but returns nothing would otherwise spin to the cap.
    if (!result.hasNext || result.plans.length === 0) break;
  }

  return plans;
}

/**
 * One plan, by the `public_id` a listed row carries.
 *
 * The response is a single plan in exactly the shape a row of the list is
 * in, so both go through `toBrandPlan` rather than each keeping a copy of
 * the mapping.
 *
 * Nothing on the Plans tab calls this yet: the list already carries every
 * field the table and the edit dialog use, so re-reading a row before opening
 * it would be a request for something in hand. It is what a detail screen, or
 * a dialog that wanted to be sure it was editing the current version, would
 * open with.
 */
export async function fetchPlan(
  getToken: GetToken,
  publicId: string,
  signal?: AbortSignal
): Promise<BrandPlan> {
  const response = await api.get<PlanResponse>(
    API_ENDPOINTS.plans.detail(publicId),
    { token: await token(getToken, "load this plan"), signal }
  );

  return toBrandPlan(response);
}

/** Creates a plan and returns the row the backend stored. */
export async function createPlan(
  getToken: GetToken,
  draft: NewBrandPlan,
  signal?: AbortSignal
): Promise<BrandPlan> {
  const body: PlanRequest = {
    name: draft.name,
    duration: draft.durationId,
    price_amount: toAmount(draft.price),
    // Sent whether or not they were filled in: on a create there is nothing
    // stored to leave alone, so an unanswered count goes up as the `null` it is.
    consultations_count: draft.consultations,
    meal_plans_count: draft.mealPlans,
    meal_options_per_meal: draft.mealOptionsPerMeal,
    support_channels: draft.supportChannels,
  };

  const response = await api.post<PlanResponse>(
    API_ENDPOINTS.plans.list,
    body,
    { token: await token(getToken, "add a plan"), signal }
  );

  return toBrandPlan(response);
}

/**
 * Lists a plan on the public page, or takes it off again.
 *
 * Its own pair of endpoints rather than a field on the `PATCH`, so this sends
 * no body at all - the path is the whole of the request. The response is the
 * plan as it now stands, which is what the row is rebuilt from: the
 * backend's `is_active` decides the pill, not the boolean asked for.
 */
export async function setPlanActive(
  getToken: GetToken,
  publicId: string,
  active: boolean,
  signal?: AbortSignal
): Promise<BrandPlan> {
  const path = active
    ? API_ENDPOINTS.plans.activate(publicId)
    : API_ENDPOINTS.plans.deactivate(publicId);

  const response = await api.post<PlanResponse>(path, undefined, {
    token: await token(
      getToken,
      active ? "activate this plan" : "deactivate this plan"
    ),
    signal,
  });

  return toBrandPlan(response);
}

/**
 * Whether two channel lists hold the same answers.
 *
 * Order is not part of the answer: the dialog keeps the channels in the order
 * they were ticked, and someone who unticked Email and ticked it again has not
 * changed what the plan offers — only the order this side happens to hold
 * them in. Comparing as lists would send a `PATCH` for that.
 */
const sameChannels = (a: SupportChannel[], b: SupportChannel[]) =>
  a.length === b.length && a.every((channel) => b.includes(channel));

/**
 * What an edit would change, which is all a `PATCH` sends. Empty where the
 * dialog was opened and closed again without anything being touched.
 */
const changesBetween = (
  plan: BrandPlan,
  draft: NewBrandPlan
): PlanRequest => {
  const changes: PlanRequest = {};

  if (draft.name !== plan.name) changes.name = draft.name;
  if (draft.durationId !== plan.durationId) {
    changes.duration = draft.durationId;
  }
  if (draft.price !== plan.price) {
    changes.price_amount = toAmount(draft.price);
  }
  // A count cleared back to blank is a change like any other, and goes up as
  // `null` — which is why these compare rather than test for truthiness.
  if (draft.consultations !== plan.consultations) {
    changes.consultations_count = draft.consultations;
  }
  if (draft.mealPlans !== plan.mealPlans) {
    changes.meal_plans_count = draft.mealPlans;
  }
  if (draft.mealOptionsPerMeal !== plan.mealOptionsPerMeal) {
    changes.meal_options_per_meal = draft.mealOptionsPerMeal;
  }
  if (!sameChannels(draft.supportChannels, plan.supportChannels)) {
    changes.support_channels = draft.supportChannels;
  }

  return changes;
};

/**
 * Saves an edit, and returns the row as the backend now holds it.
 *
 * A dialog that was opened and closed with nothing altered sends nothing and
 * answers with the plan unchanged: an empty `PATCH` is a request that can
 * only fail or do nothing, and either way it is one the user did not ask for.
 */
export async function updatePlan(
  getToken: GetToken,
  plan: BrandPlan,
  draft: NewBrandPlan,
  signal?: AbortSignal
): Promise<BrandPlan> {
  const changes = changesBetween(plan, draft);
  if (Object.keys(changes).length === 0) return plan;

  const response = await api.patch<PlanResponse>(
    API_ENDPOINTS.plans.detail(plan.id),
    changes,
    { token: await token(getToken, "save this plan"), signal }
  );

  return toBrandPlan(response);
}
